/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import {
  Activity,
  ArrowRightLeft,
  Bot,
  ClipboardCheck,
  Cloud,
  FileText,
  FolderKanban,
  History,
  LayoutDashboard,
  LayoutGrid,
  Rocket,
  Server,
  ShieldCheck,
  ShieldHalf,
  Users,
  type LucideIcon,
} from "lucide-react";

import type { IconTileTone } from "@src/components/ui/IconTile/IconTile";

/*
 * Swift's sections, in the AWS Management module's pattern (NAV_GROUPS): the sidebar lists them
 * flat, a group with several pages (Administration) is entered rather than expanded, and the
 * Overview page shows each one as a card. The sidebar, the top bar's breadcrumbs and the Overview
 * page all read this one list, so they never disagree.
 */
export type SectionItem = {
  /** The first path segment, also the `navigationMenu` value (disabledPages, admin checks). */
  value: string;
  label: string;
  icon: LucideIcon;
};
export type Section = {
  id: string;
  label: string;
  icon: LucideIcon;
  /** One line for the Overview card. */
  subtitle: string;
  tone?: IconTileTone;
  items: SectionItem[];
  adminOnly?: boolean;
};

export const HUB_ID = "hub";

export const SECTIONS: Section[] = [
  {
    id: HUB_ID,
    label: "Overview",
    icon: LayoutGrid,
    subtitle: "Every SwiftIQ section",
    items: [{ value: "", label: "Overview", icon: LayoutGrid }],
  },
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    subtitle: "Migration activity at a glance",
    items: [{ value: "dashboard", label: "Dashboard", icon: LayoutDashboard }],
  },
  {
    id: "live",
    label: "Live monitor",
    icon: Activity,
    subtitle: "Transfers and deployments running now",
    items: [{ value: "live", label: "Live monitor", icon: Activity }],
  },
  {
    id: "transfers",
    label: "Transfers",
    icon: ArrowRightLeft,
    tone: "info",
    subtitle: "Replicate and migrate workloads",
    items: [{ value: "transfers", label: "Transfers", icon: ArrowRightLeft }],
  },
  {
    id: "deployments",
    label: "Deployments",
    icon: Rocket,
    subtitle: "Cut-overs to the target cloud",
    items: [{ value: "deployments", label: "Deployments", icon: Rocket }],
  },
  {
    id: "cutovers",
    label: "Change control",
    icon: ClipboardCheck,
    tone: "warning",
    subtitle: "Approve cut-overs and their change windows",
    items: [{ value: "cutovers", label: "Change control", icon: ClipboardCheck }],
  },
  {
    id: "dr",
    label: "Disaster recovery",
    icon: ShieldHalf,
    tone: "success",
    subtitle: "Protect VMs to a DR site and watch their RPO",
    items: [{ value: "dr", label: "Disaster recovery", icon: ShieldHalf }],
  },
  {
    id: "endpoints",
    label: "Cloud endpoints",
    icon: Cloud,
    subtitle: "The clouds SwiftIQ connects to",
    items: [{ value: "endpoints", label: "Cloud endpoints", icon: Cloud }],
  },
  {
    id: "minion-pools",
    label: "SwiftIQ Agents",
    icon: Bot,
    subtitle: "Helper machines that read and write disks",
    items: [{ value: "minion-pools", label: "SwiftIQ Agents", icon: Bot }],
  },
  {
    id: "bare-metal-servers",
    label: "On-prem servers",
    icon: Server,
    subtitle: "Physical servers registered for migration",
    items: [{ value: "bare-metal-servers", label: "On-prem servers", icon: Server }],
  },
  {
    id: "administration",
    label: "Administration",
    icon: ShieldCheck,
    subtitle: "Projects, Users, Audit history, Logs",
    adminOnly: true,
    items: [
      { value: "projects", label: "Projects", icon: FolderKanban },
      { value: "users", label: "Users", icon: Users },
      { value: "audit", label: "Audit history", icon: History },
      { value: "logging", label: "Logs", icon: FileText },
    ],
  },
];

/** Pages reached from a section without an entry of their own. */
const EXTRA_PAGES: Record<string, { section: string; label: string }> = {
  wizard: { section: "transfers", label: "New transfer" },
  streamlog: { section: "administration", label: "Log stream" },
};

export const pathSection = (pathname: string): string =>
  pathname.split("/").filter(Boolean)[0] || "";

/** The section a page (first path segment, or a `currentPage` value) belongs to. */
export const sectionFor = (page: string): Section | undefined => {
  if (page === "" || page === HUB_ID) return SECTIONS[0];
  const extra = EXTRA_PAGES[page];
  if (extra) return SECTIONS.find(s => s.id === extra.section);
  return SECTIONS.find(s => s.items.some(i => i.value === page));
};

export const itemFor = (page: string): SectionItem | undefined => {
  const extra = EXTRA_PAGES[page];
  if (extra) {
    const section = SECTIONS.find(s => s.id === extra.section)!;
    return { value: page, label: extra.label, icon: section.icon };
  }
  return SECTIONS.flatMap(s => s.items).find(i => i.value === page && page !== "");
};

/**
 * The sections this user sees: admin-only ones only for administrators, and never a page listed in
 * the config's `disabledPages`. A group left with no pages is dropped.
 */
export const visibleSections = (isAdmin: boolean, disabledPages: string[]): Section[] =>
  SECTIONS.filter(s => !s.adminOnly || isAdmin)
    .map(s => ({
      ...s,
      items: s.items.filter(i => !i.value || !disabledPages.includes(i.value)),
    }))
    .filter(s => s.items.length > 0);

export const sectionPath = (section: Section) => `/${section.items[0].value}`;
